/**
 * Native Google One-Tap sign-in via capacitor-native-google-one-tap-signin.
 *
 * Replaces the old browser-redirect flow with a 1-tap native bottom-sheet:
 *   - First time: Google's account picker appears (One-Tap-style)
 *   - Subsequent times: silent auto-sign-in OR a one-tap prompt
 *
 * On success the Google ID token is POSTed to /api/auth/google-native, which
 * verifies the token's signature with Google's public keys and issues our
 * session_token cookie + Bearer header. Same downstream behaviour as the
 * existing email/password and Emergent browser login flows.
 *
 * Prerequisites:
 *   - REACT_APP_GOOGLE_WEB_CLIENT_ID must be set at build time (.env)
 *   - Google Cloud Console must have:
 *       - Web client ID == REACT_APP_GOOGLE_WEB_CLIENT_ID
 *       - Android client ID with package=com.fitjong.app + each SHA-1 fingerprint
 *         (debug + release + Play App Signing key)
 */
import { Capacitor } from "@capacitor/core";
import { GoogleOneTapAuth } from "capacitor-native-google-one-tap-signin";
import { api } from "./api";
import { setSessionToken } from "./sessionStore";

const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || "";

let _initialized = false;

function ensureInitialized() {
  if (_initialized) return;
  if (!WEB_CLIENT_ID) {
    console.warn("[google-onetap] REACT_APP_GOOGLE_WEB_CLIENT_ID not set");
    return;
  }
  try {
    // Fire-and-forget per plugin docs: "Other methods wait till initialize
    // is finished so you must not await initialize."
    GoogleOneTapAuth.initialize({ clientId: WEB_CLIENT_ID });
    _initialized = true;
  } catch (e) {
    console.warn("[google-onetap] initialize failed:", e);
  }
}

/**
 * Is the native plugin available + initialized? Used by Login.jsx to decide
 * whether to render the "Continue with Google" button.
 */
export function isGoogleOneTapAvailable() {
  if (!WEB_CLIENT_ID) return false;
  try {
    if (!Capacitor.isNativePlatform()) return false;
  } catch (_) {
    return false;
  }
  return true;
}

/**
 * Attempt one-tap or button-flow sign-in. Returns `{ ok: true, profile }`
 * on success, or `{ ok: false, reason }` on any failure.
 *
 * Caller (Login.jsx) updates AuthContext + navigates to /dashboard on ok.
 *
 * Iter76c — Tester report: after Iter73b forced button-flow + signOut on
 * every tap, the account picker briefly appeared but then dropped to a
 * white screen ("you fixed one thing and broke another"). The plugin's
 * `signInWithGoogleButtonFlowForNativePlatform` is documented to be
 * called AFTER `tryAutoOrOneTapSignIn` returns `!isSuccess`, not as a
 * standalone first call. Reverted to the original priming pattern:
 *
 *   tryAutoOrOneTapSignIn  →  (isSuccess) return token
 *                          →  (no-success) signInWithGoogleButtonFlowForNativePlatform
 *
 * For the "wrong Gmail account auto-picked" case, see
 * `signInWithGoogleNativePickAccount()` below — Login.jsx exposes a
 * separate "Use a different Google account" link that calls it.
 */
export async function signInWithGoogleNative({ forcePicker = false } = {}) {
  if (!isGoogleOneTapAvailable()) {
    return { ok: false, reason: "unavailable" };
  }
  ensureInitialized();

  let idToken = null;
  try {
    let result;
    if (forcePicker) {
      // Explicit "Use a different Google account" path. Sign-out first
      // so the system Credential Manager picker is guaranteed to show.
      try { await GoogleOneTapAuth.signOut(); } catch (_) {}
      result = await GoogleOneTapAuth.signInWithGoogleButtonFlowForNativePlatform();
    } else {
      // Default / first-tap path. tryAutoOrOneTap shows a bottom-sheet
      // chooser on first install (so the user CAN pick the right
      // account here) and silently auto-signs in on subsequent runs.
      result = await GoogleOneTapAuth.tryAutoOrOneTapSignIn();
      if (!result?.isSuccess) {
        // User dismissed the one-tap prompt OR no eligible account
        // → fall back to the explicit button flow.
        result = await GoogleOneTapAuth.signInWithGoogleButtonFlowForNativePlatform();
      }
    }
    if (!result?.isSuccess || !result?.success) {
      const reason = result?.noSuccess?.noSuccessReasonCode || "no-success";
      console.warn("[google-onetap] sign-in dismissed:", reason);
      return { ok: false, reason };
    }
    idToken = result.success.idToken;
  } catch (e) {
    console.warn("[google-onetap] sign-in threw:", e?.message || e);
    return { ok: false, reason: "plugin-error", detail: String(e?.message || e) };
  }

  if (!idToken) {
    return { ok: false, reason: "no-id-token" };
  }

  // Exchange Google ID token for our session token on the backend.
  // Same pattern as nativeAuth.js iter54: when Android resumes the WebView
  // from the system Credential Manager sheet, the network stack sometimes
  // isn't fully reconnected for ~300-800ms, so the first POST raises
  // `AxiosError: Network Error`. Retry once with a small backoff before
  // surfacing the failure to the user.
  const exchangeWithRetry = async () => {
    try {
      return await api.post("/auth/google-native", { id_token: idToken });
    } catch (firstErr) {
      const isNetwork = firstErr?.message === "Network Error"
        || firstErr?.code === "ERR_NETWORK"
        || !firstErr?.response;
      if (!isNetwork) throw firstErr;
      console.warn("[google-onetap] backend exchange Network Error — retrying in 1.0s");
      await new Promise((r) => setTimeout(r, 1000));
      return await api.post("/auth/google-native", { id_token: idToken });
    }
  };

  try {
    const { data } = await exchangeWithRetry();
    if (data?.session_token) {
      await setSessionToken(data.session_token);
    }
    return { ok: true, user: data?.user, profile: data?.profile };
  } catch (e) {
    const errMsg = e?.response?.data?.detail || e?.message || String(e);
    console.warn("[google-onetap] backend exchange failed:", errMsg);
    return { ok: false, reason: "backend-error", detail: errMsg };
  }
}

/**
 * Sign-out helper. Calls the plugin's signOut so the next sign-in shows the
 * account chooser rather than silently auto-signing-in with the cached account.
 * Safe to call even if user signed in via email/password — the plugin no-ops.
 */
export async function signOutGoogleNative() {
  if (!isGoogleOneTapAvailable()) return;
  try {
    await GoogleOneTapAuth.signOut();
  } catch (_) {}
}
